import React from "react";
import { AreaChart, Area, ComposedChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

export function SignalChart({ sig, color = "#D4AF37", height = 120 }) {
  const entry = (sig.entry_low + sig.entry_high) / 2;
  const span = Math.abs(sig.tp1 - entry) || Math.abs(sig.sl - entry) || entry * 0.01;
  let p = entry - (sig.entry_high - sig.entry_low);
  const drift = (sig.tp1 - entry) / 22 * 0.7;
  let seed = (sig.id || "x").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  const rand = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };
  const data = Array.from({ length: 22 }, (_, t) => { p += drift + (rand() - 0.5) * span * 0.4; return { t, p: Number(p.toFixed(2)) }; });
  const lows = [sig.sl, sig.tp3, ...data.map((d) => d.p)];
  const dmin = Math.min(...lows), dmax = Math.max(...lows);
  const pad = (dmax - dmin) * 0.08 || 1;
  const rl = (y, c) => <ReferenceLine key={c + y} y={y} stroke={c} strokeWidth={1} strokeDasharray="4 3" ifOverflow="extendDomain" />;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{ top: 4, right: 46, left: 4, bottom: 0 }}>
        <defs>
          <linearGradient id={`sg${sig.id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.25} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <XAxis dataKey="t" hide />
        <YAxis domain={[dmin - pad, dmax + pad]} orientation="right" width={44} tick={{ fill: "#64748B", fontSize: 9, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
        <Area type="monotone" dataKey="p" stroke={color} strokeWidth={2} fill={`url(#sg${sig.id})`} isAnimationActive={false} />
        {rl(entry, "#94A3B8")}
        {rl(sig.sl, "#EF4444")}
        {rl(sig.tp1, "#10B981")}
        {rl(sig.tp2, "#10B981")}
        {rl(sig.tp3, "#059669")}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export function PriceSpark({ data, color = "#D4AF37", height = 60 }) {
  const id = "g" + color.replace("#", "");
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.35} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area type="monotone" dataKey="p" stroke={color} strokeWidth={2} fill={`url(#${id})`} isAnimationActive={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

const TT = ({ active, payload }) =>
  active && payload?.length ? (
    <div className="surface px-2 py-1 text-xs font-mono text-white">{Number(payload[0].value).toLocaleString()}</div>
  ) : null;

export function PriceChart({ data, color = "#D4AF37", height = 260 }) {
  const id = "gc" + color.replace("#", "");
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 4 }}>
        <defs>
          <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.28} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <XAxis dataKey="t" hide />
        <YAxis domain={["auto", "auto"]} orientation="right" tick={{ fill: "#64748B", fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} width={54} />
        <Tooltip content={<TT />} />
        <Area type="monotone" dataKey="p" stroke={color} strokeWidth={2} fill={`url(#${id})`} isAnimationActive={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
}
